import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAuth } from './useAuth'

/**
 * Side-effect-only hook — renders nothing.
 *
 * Clears the React Query cache whenever the authenticated user identity
 * changes (sign-out or account switch on the same browser tab). The first
 * observed uid after mount (normal startup / loading → first sign-in) is
 * intentionally skipped so a legitimately-warming cache is not cleared on
 * every page load.
 *
 * Mount this once inside <QueryClientProvider> (a descendant of it so
 * useQueryClient() resolves) and above any query-bearing components.
 */
export function useClearQueryCacheOnUserChange(): void {
  const { state } = useAuth()
  const queryClient = useQueryClient()

  const uid = state.status === 'signed-in' ? state.user.uid : (state.status === 'loading' ? undefined : null)

  // undefined = still loading (skip), string = signed in, null = signed out
  const prevUidRef = useRef<string | null | undefined>(undefined)
  const isFirstSettledRef = useRef(true)

  useEffect(() => {
    // While auth is still loading, do nothing
    if (uid === undefined) return

    if (isFirstSettledRef.current) {
      // Record the first settled value without clearing
      isFirstSettledRef.current = false
      prevUidRef.current = uid
      return
    }

    if (uid !== prevUidRef.current) {
      prevUidRef.current = uid
      queryClient.clear()
    }
  }, [uid, queryClient])
}
